// What the Floor draws for each station, from the reads it already has (spec/13 §3): the live floor
// snapshot (presence: online, mode, status) and, for a station in session, that station's session
// (`GET …/stations/{s}/session`) — its time left, counted down on the client against when it was read,
// as spec/05 §4.4 asks — and the name of the player on it (`GET …/players/{p}`, one cached read per player
// in session, with `players:read`). Nothing here computes money or bills time; it only decides which of
// the brand's station states a tile wears (BRAND.md §4.5) and who it names.

import type { FloorStationState, OrganizationPlayerProfile, Session } from '@clavian/api-client';
import { useQueries } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useApi } from '../api/client';
import type { BootTileState } from '../boot/floor-boot';
import { PROBLEM, problemCode } from '../api/problems';
import { useRealtimeLive } from '../realtime';
import { playerKeys, playerName } from '../org/players/queries';
import { sessionKeys } from '../org/sessions/queries';

/** "Ending soon" is ten minutes or less (spec/13 §3, BRAND.md §4.5). */
export const ENDING_SOON_SECONDS = 600;

/** How often a live figure on the floor re-renders: the clocks are drawn to the minute. */
const TICK_MS = 15_000;

/** A station's state on the floor — the words and the colour a tile wears. */
export type FloorState =
  | 'session'
  | 'ending'
  | 'overdue'
  | 'locked'
  | 'free'
  | 'offline'
  | 'maintenance'
  | 'disabled'
  | 'updating'
  | 'settingUp'
  | 'pairing'
  /** A PC with no Clavian Station app (`client: none`, ADR-0065): no session, clock or presence — only how it starts. */
  | 'none';

export interface FloorStationView {
  readonly station: FloorStationState;
  readonly state: FloorState;
  /** The session running on it, when the role can read one. */
  readonly session: Session | null | undefined;
  /** Seconds of funded time left, counted down since the read; null without a session. */
  readonly secondsLeft: number | null;
  /** 0–1: the share of the session's time still left, for the ring. */
  readonly fraction: number;
  /** When this view was drawn (ms): what the now-playing line counts its minutes against (F-CAT-04). */
  readonly now: number;
  /**
   * Who is playing, in words: the player's name, "Walk-in" for a console booked without one (ADR-0034), or null — a
   * free station, a role without `players:read`, or a name not read yet (spec/13 §3.1).
   */
  readonly playerName: string | null;
  /** How the PC starts from the boot server (Clavian Boot), when it does and the member may see it. */
  readonly boot?: BootTileState | undefined;
}

/** A station with no Clavian Station app (`client: none`): a Boot-only PC, or one still waiting for the app. */
export function hasNoClient(station: Pick<FloorStationState, 'client'>): boolean {
  return station.client === 'none';
}

/**
 * Whether a station is run by staff (ADR-0033): a console, a sim, a table with no app. It holds no socket, so it is never
 * offline — the api gives its mode from its session.
 */
export function isStaffRun(station: Pick<FloorStationState, 'managed'>): boolean {
  return station.managed === false;
}

/** The mode a station reports that means someone is on it. */
export function isOccupied(station: FloorStationState): boolean {
  return !hasNoClient(station) && (station.online || isStaffRun(station)) && station.status !== 'disabled' && (station.mode === 'in_session' || station.mode === 'session_locked');
}

/** The current time, re-read every `TICK_MS` while mounted: the floor's clocks count down with it. */
export function useNow(intervalMs = TICK_MS): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

/**
 * The sessions on the occupied stations, one read each — the same query (and cache entry) the station modal
 * uses, so they are read once and kept live by the same push. A free station is never read.
 */
export function useFloorSessions(organizationId: string, venueId: string, stations: readonly FloorStationState[], enabled: boolean) {
  const api = useApi();
  const live = useRealtimeLive();
  const occupied = stations.filter(isOccupied);
  const results = useQueries({
    queries: occupied.map((station) => ({
      queryKey: sessionKeys.onStation(organizationId, venueId, station.stationId),
      enabled,
      refetchInterval: live ? (false as const) : 10_000,
      queryFn: async (): Promise<Session | null> => {
        try {
          return await api.getStationSession({ path: { organizationId, venueId, stationId: station.stationId } });
        } catch (error) {
          if (problemCode(error) === PROBLEM.notFound) return null;
          throw error;
        }
      },
    })),
  });
  const byStation = new Map<string, { readonly session: Session | null | undefined; readonly readAt: number }>();
  occupied.forEach((station, index) => {
    const result = results[index];
    byStation.set(station.stationId, { session: result?.data, readAt: result?.dataUpdatedAt ?? 0 });
  });
  return byStation;
}

/** A name rarely changes while someone plays: the floor reads each player's profile at most this often. */
const PLAYER_NAME_STALE_MS = 5 * 60_000;

/**
 * The names of the players in session on the floor (spec/13 §3.1: the tile names who is playing), by player id: one
 * read per distinct player in a running session — never one per station — through the Players screen's own profile
 * read and cache entry, so opening the player there costs nothing more. `enabled` is `players:read`; without it the
 * map stays empty and the floor names nobody. A walk-in (ADR-0034) has no player to read.
 */
export function useFloorPlayerNames(
  organizationId: string,
  sessions: ReadonlyMap<string, { readonly session: Session | null | undefined }>,
  enabled: boolean,
): ReadonlyMap<string, string> {
  const api = useApi();
  const ids = [
    ...new Set(
      [...sessions.values()].flatMap(({ session }) =>
        // A walk-in (ADR-0034) and a guest (F-SES-07) have no player profile to read.
        session === null || session === undefined || session.status === 'ended' || session.walkIn === true || session.guest !== undefined
          ? []
          : [session.playerId],
      ),
    ),
  ].sort();
  const results = useQueries({
    queries: ids.map((playerId) => ({
      queryKey: playerKeys.profile(organizationId, playerId),
      enabled,
      staleTime: PLAYER_NAME_STALE_MS,
      queryFn: (): Promise<OrganizationPlayerProfile> => api.getPlayer({ path: { organizationId, playerId } }),
    })),
  });
  const names = new Map<string, string>();
  ids.forEach((playerId, index) => {
    const profile = results[index]?.data;
    if (enabled && profile !== undefined) names.set(playerId, playerName(profile));
  });
  return names;
}

/**
 * Who a view's session is for, in words, from the names read (`useFloorPlayerNames`); null when not known. A walk-in
 * reads `walkIn`; a guest (F-SES-07) reads `guest(code)` — "Guest · K7PX2M" (a running session is never a claimed one).
 */
export function playerNameOf(
  session: Session | null | undefined,
  names: ReadonlyMap<string, string>,
  walkIn: string,
  guest: (code: string) => string = (code) => code,
): string | null {
  if (session === null || session === undefined || session.status === 'ended') return null;
  if (session.walkIn === true) return walkIn;
  if (session.guest !== undefined) return guest(session.guest.code);
  return names.get(session.playerId) ?? null;
}

/** Seconds left on a session now, from what it had when it was read. Never below zero. */
export function secondsLeftNow(session: Session, readAt: number, now: number): number {
  if (session.status === 'grace' || session.status === 'ended') return 0;
  const elapsed = readAt > 0 ? Math.max(0, Math.floor((now - readAt) / 1000)) : 0;
  return Math.max(0, session.timeLeftSeconds - elapsed);
}

/** The station's floor state and its clock. */
export function viewOf(
  station: FloorStationState,
  entry: { readonly session: Session | null | undefined; readonly readAt: number } | undefined,
  now: number,
): FloorStationView {
  const base = { station, session: entry?.session, secondsLeft: null, fraction: 0, now, playerName: null } as const;
  if (station.status === 'disabled') return { ...base, state: 'disabled' };
  // No app, so no presence to lose and no session to run: the tile says how the PC starts.
  if (hasNoClient(station)) return { ...base, state: 'none' };
  // A staff-run station (ADR-0033) has no socket to lose: it is never offline.
  // A station nothing is paired to yet — just made from a new PC (ADR-0065) — is waiting to pair, not offline. The snapshot
  // gives an offline station no mode, so `paired: false` is what says it (an older snapshot without it reads offline).
  if (!station.online && !isStaffRun(station)) return { ...base, state: station.paired === false ? 'pairing' : 'offline' };
  switch (station.mode) {
    case 'maintenance':
      return { ...base, state: 'maintenance' };
    case 'updating':
      return { ...base, state: 'updating' };
    case 'setting_up':
      return { ...base, state: 'settingUp' };
    case 'pairing':
      return { ...base, state: 'pairing' };
    case 'in_session':
    case 'session_locked': {
      const session = entry?.session;
      if (session === undefined || session === null) {
        return { ...base, state: station.mode === 'session_locked' ? 'locked' : 'session' };
      }
      // An Open console session (ADR-0034) has no end: no countdown, never "ending" — it runs until staff end it.
      if (session.booking?.open === true && session.status !== 'grace') {
        return { ...base, state: station.mode === 'session_locked' || session.status === 'locked' ? 'locked' : 'session', secondsLeft: null, fraction: 1 };
      }
      const left = secondsLeftNow(session, entry?.readAt ?? 0, now);
      const started = new Date(session.startedAt).getTime();
      const elapsed = Number.isNaN(started) ? 0 : Math.max(0, (now - started) / 1000);
      const fraction = left + elapsed > 0 ? left / (left + elapsed) : 0;
      const state: FloorState =
        session.status === 'grace'
          ? 'overdue'
          : station.mode === 'session_locked' || session.status === 'locked'
            ? 'locked'
            : left <= ENDING_SOON_SECONDS
              ? 'ending'
              : 'session';
      return { ...base, state, secondsLeft: left, fraction };
    }
    default:
      return { ...base, state: 'free' };
  }
}

/** `H:MM` from whole seconds — how time left reads on a tile (spec/05 §4.4). */
export function clockOf(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
}

/** The local clock time of an RFC 3339 instant, `HH:MM`. */
export function timeOfDay(iso: string | null): string | null {
  if (iso === null) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
}
